/**
 * Tiny IndexedDB wrapper for persisting pending session-audio uploads.
 *
 * Two stores live in the same DB: `meta` (small JSON records keyed by id) and
 * `blobs` (the actual audio Blob keyed by id). Split so listing drafts doesn't
 * drag every audio blob into memory.
 */

const DB_NAME = 'scribenza_drafts'
const DB_VERSION = 1
const META_STORE = 'meta'
const BLOB_STORE = 'blobs'

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror = () => reject(req.error)
    req.onsuccess = () => resolve(req.result)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE)
      }
    }
  })
  return dbPromise
}

function tx(storeNames, mode) {
  return openDb().then(db => {
    const t = db.transaction(storeNames, mode)
    const done = new Promise((resolve, reject) => {
      t.oncomplete = () => resolve()
      t.onerror = () => reject(t.error)
      t.onabort = () => reject(t.error)
    })
    return { t, done }
  })
}

function req(r) {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

export const draftsDb = {
  async listMeta() {
    const { t } = await tx([META_STORE], 'readonly')
    const all = await req(t.objectStore(META_STORE).getAll())
    return (all || []).sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  },

  async getMeta(id) {
    const { t } = await tx([META_STORE], 'readonly')
    return req(t.objectStore(META_STORE).get(id))
  },

  async putMeta(meta) {
    const { t, done } = await tx([META_STORE], 'readwrite')
    t.objectStore(META_STORE).put(meta)
    await done
    return meta
  },

  async putBlob(id, blob) {
    const { t, done } = await tx([BLOB_STORE], 'readwrite')
    t.objectStore(BLOB_STORE).put(blob, id)
    await done
  },

  async getBlob(id) {
    const { t } = await tx([BLOB_STORE], 'readonly')
    return req(t.objectStore(BLOB_STORE).get(id))
  },

  async remove(id) {
    const { t, done } = await tx([META_STORE, BLOB_STORE], 'readwrite')
    t.objectStore(META_STORE).delete(id)
    t.objectStore(BLOB_STORE).delete(id)
    await done
  },

  async clearAll() {
    const { t, done } = await tx([META_STORE, BLOB_STORE], 'readwrite')
    t.objectStore(META_STORE).clear()
    t.objectStore(BLOB_STORE).clear()
    await done
  },
}
